/**
 * launchVideoCall — permission-gated launch helper for the 1:1 chat video call.
 *
 * Web port of Flutter `goToVideoCallScreen` (`chat_screen.dart:891-929`): mint
 * the permission gate, then (on grant) navigate to the call screen. Flutter
 * requests `[Permission.camera, Permission.microphone]` and, if any status is
 * restricted / denied / permanently-denied, shows a "grant permission" toast and
 * does NOT navigate (`:911-914`); otherwise it navigates (`:916-927`).
 *
 * On the web the permission request is the browser prompt fired by
 * `navigator.mediaDevices.getUserMedia({ video: true, audio: true })`. A thrown
 * error (`NotAllowedError` for a denied prompt, `NotFoundError` for a missing
 * device, etc.) is the web analog of Flutter's denied / restricted statuses —
 * we toast and bail without navigating. On grant we immediately stop the probe
 * tracks so the camera/mic indicator does not linger before the page mounts; the
 * `VideoCallPage` re-acquires the devices via the Agora hooks (`useVideoCall`).
 *
 * This is a plain presentation utility — it does NOT call React hooks. The
 * caller (the chat page, which already has `useLocale()`) passes the resolved
 * `l10n` and the full call context, exactly as Flutter threads the call context
 * through `NavigationService.navigateToVideoCallScreen`.
 */
import toast from 'react-hot-toast';

import { appLogger } from '@/domain/services/analytics/appLogger';
import { Navigator } from '@/presentation/routing/Navigator';
import type { l10n } from '@/presentation/locale/l10n';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';

/**
 * Args mirror Flutter `goToVideoCallScreen(messageId, callerUser, audienceUser,
 * callerUserStreamingId)` (`chat_screen.dart:891-896`) plus the `chatUsersInfo`
 * the web save path needs. `chatUsersInfo` supplies the Agora channel id
 * (`groupChatId`, Flutter `widget.groupChatId`) and rides `location.state` to
 * `VideoCallPage` via `Navigator.navigateToVideoCall` (Task 12).
 */
export interface LaunchVideoCallArgs {
  /** The call initiator (Flutter `callerUser`). */
  callerUser: UserEntity;
  /** The conversation peer / call recipient (Flutter `audienceUser`). */
  audienceUser: UserEntity;
  /** Chat-users context; carries `groupChatId` (the Agora channel) + save-path metadata. */
  chatUsersInfo: ChatUsersInfoEntity;
  /** The call session id stored on the message as `videoCallId` (Flutter `messageId`). */
  callMessageId: string;
  /**
   * The caller's local streaming id forwarded to the audience (Flutter
   * `callerUserStreamingId`). `undefined` on the caller-initiate path.
   */
  callerUserStreamingId?: number;
  /** Resolved localizations from the caller (no hooks in this plain helper). */
  l10n: l10n;
}

/**
 * Probe camera + mic permission, then navigate to the video-call screen.
 *
 * Parity with Flutter `goToVideoCallScreen` (`chat_screen.dart:891-929`):
 * - grant  → `Navigator.navigateToVideoCall(...)` (web analog of
 *   `NavigationService.navigateToVideoCallScreen`, `:916-927`).
 * - denial → toast `permissionDeniedToast` and return WITHOUT navigating
 *   (`:911-914`).
 */
export async function launchVideoCall(args: LaunchVideoCallArgs): Promise<void> {
  const { callerUser, audienceUser, chatUsersInfo, callMessageId } = args;

  try {
    // The web permission request: the browser prompt fired by creating the
    // local tracks (Flutter `:898-899`). We only need it to surface the prompt,
    // so stop the tracks immediately — the page re-acquires them via Agora.
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    stream.getTracks().forEach((track) => track.stop());
  } catch (error) {
    // Denied / no device → Flutter `:911-914`: toast + no navigation.
    appLogger.error(
      'camera/mic permission denied; not launching video call',
      'launchVideoCall',
      {
        context: { callMessageId },
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );
    const message = args.l10n.chat.videoCallPage.permissionDeniedToast.replace(
      /\{permission\}/g,
      args.l10n.chat.videoCallPage.permissionDeniedDevices,
    );
    toast.error(message);
    return;
  }

  // Grant → navigate (Flutter `:916-927`).
  Navigator.navigateToVideoCall({
    callerUser,
    audienceUser,
    groupChatId: chatUsersInfo.groupChatId ?? '',
    callMessageId,
    callerUserStreamingId: args.callerUserStreamingId,
    chatUsersInfo,
  });
}
